import { ConfigStore } from "../lib/config/config";
import { IEventEmitter, IEventSource } from "../lib/eventing/events";
import { ExcelEvents, ExcelEventSource, Workbook, Worksheet } from "./excelEvents";
import { WorkbookClassificationEvents, WorkbookClassificationSource } from "./workbookClassification";

export type WorksheetClassificationEvents = {
    worksheetClassified: WorksheetClassification & { workbookId: string, worksheetId: string, restored: boolean },
    worksheetUnclassified: { workbookId: string, worksheetId: string, considered: string[] },
    worksheetClassificationConflict: { workbookId: string, worksheetId: string, claimed: string[], considered: string[] }
}

export type WorksheetClassificationEmitter = IEventEmitter<WorksheetClassificationEvents>;
export type WorksheetClassificationSource = IEventSource<WorksheetClassificationEvents>;

export type WorksheetClassification = {
    timestamp: number;
    code: string;
    rule: string;
    considered: string[];
}

export type WorksheetClassificationRule<TRule extends string = string, TInputs extends Record<string,any> = Record<string, any>> = {
    name: TRule;
    description: string;
    inputs: TInputs,
    looseCriteria: (worksheet: Worksheet, workbookDetails: Workbook) => boolean;
    tightCriteria: (worksheet: Worksheet, workbookDetails: Workbook, inputs:TInputs) => boolean;
}

export type WorksheetClassificationRuleMap = Record<string, WorksheetClassificationRule>;

export type WorksheetClassificationStore = ConfigStore<Record<string, WorksheetClassification>>;

export type WorkbookWorksheetClasificationRuleFetcher = (workbookCode:string) => Promise<WorksheetClassificationRuleMap|undefined>;

export class WorksheetClassifier {
    private initialWorkbookDetails: Workbook;
    private workbookClassificationCode:string;
    private workbookWorksheetRules: WorksheetClassificationRuleMap;
    private worksheetClassifications: Record<string, WorksheetClassification> = {};

    constructor(
        private readonly classificationRulesFetcher: WorkbookWorksheetClasificationRuleFetcher,
        private readonly appEvents: ExcelEventSource,
        private readonly workbookClassificationEvents: WorkbookClassificationSource,
        private readonly classificationEmitter: WorksheetClassificationEmitter,
        private readonly classificationStore: WorksheetClassificationStore
    ) {
        this.appEvents.events.workbookOpened.on(async (payload) => this.handleWorkbookOpened(payload));
        this.workbookClassificationEvents.events.workbookClassified.on(async (payload) => await this.handleWorkbookClassified(payload));
    }

    private async handleWorkbookClassified(workbookClassification: WorkbookClassificationEvents['workbookClassified']) {
        this.workbookClassificationCode = workbookClassification.code;
        if (!this.initialWorkbookDetails) return;

        this.workbookWorksheetRules = await this.classificationRulesFetcher(this.workbookClassificationCode);
        if (!this.workbookWorksheetRules) {
            console.warn(`No worksheet classification rules found for workbook code: ${this.workbookClassificationCode}`);
            return;
        }

        this.worksheetClassifications = await this.classificationStore.load() ?? {};

        this.appEvents.events.worksheetAdded.on(async (payload) => await this.handleWorksheetAdded(payload));
        this.appEvents.events.worksheetDeleted.on(async (payload) => await this.handleWorksheetDeleted(payload));

        for (const worksheet of this.initialWorkbookDetails.worksheets) {
            await this.classifyWorksheet(worksheet);
        }
    }

    private handleWorkbookOpened(payload: ExcelEvents['workbookOpened']) {
        this.initialWorkbookDetails = payload.workbook;
    }

    private async handleWorksheetAdded(payload: ExcelEvents['worksheetAdded']) {
        await this.classifyWorksheet(payload.worksheet);
    }
    private async handleWorksheetDeleted(payload: ExcelEvents['worksheetDeleted']) {
        await this.unclassifyWorksheet(payload.worksheetId);
    }

    private async classifyWorksheet(
        worksheet: Worksheet
    ) {
        const existingClassification = await this.loadExistingClassification(this.initialWorkbookDetails.id, worksheet.id);
        
        if (!existingClassification) {
            await this.evaluateClassificationRules(worksheet);
        }
    }

    private async unclassifyWorksheet(
        worksheetId:string
    ) {
        const existingClassification = this.worksheetClassifications[worksheetId];
        const wasDeleted = delete this.worksheetClassifications[worksheetId];

        if (wasDeleted) {
            await this.classificationStore.save(this.worksheetClassifications);
            this.classificationEmitter.events.worksheetUnclassified.emit({
                workbookId: this.initialWorkbookDetails.id,
                worksheetId: worksheetId,
                considered: existingClassification?.considered ?? []
            });
        }
    }

    private async loadExistingClassification(
        workbookId: string,
        worksheetId: string
    ): Promise<WorksheetClassification | null> {
        const classifications = await this.classificationStore.load() ?? {};
        const classification = classifications[worksheetId];
        if (classification) {
            this.emitWorksheetClassification(workbookId, worksheetId, classification, true);
        }
        return classification ?? null;
    }

    private async evaluateClassificationRules(
        worksheet: Worksheet
    ) {
        const rules = this.workbookWorksheetRules;
        const applicableClassifiers = Object.entries(rules).filter(([_, rule]) => rule.looseCriteria(worksheet, this.initialWorkbookDetails));
        const applicableCodes = applicableClassifiers.map(([code, _]) => code);

        //TODO: consider pushing this out into the excel relay
        const classifierInputs = await Excel.run(async (ctx) => {
            const excelWorksheet = ctx.workbook.worksheets.getItem(worksheet.id);
            const inputRangesByRule = applicableClassifiers.reduce<Record<string, Record<string,Excel.Range>>>((acc, [_, classifier]) => {
                acc[classifier.name] = this.inputRangesForRule(excelWorksheet, classifier);
                return acc
            }, {});
            await ctx.sync();

            const inputValuesByRule = Object.entries(inputRangesByRule).reduce<Record<string,Record<string, any>>>((acc, [ruleName, rangesByInput]) => {
                acc[ruleName] = Object.entries(rangesByInput).reduce<Record<string, any>>((innerAcc, [inputName, range]) => {
                    const values = range.values;
                    innerAcc[inputName] = values.length == 1 && values[0].length == 1 ? values[0][0] : values;
                    return innerAcc;
                }, {});
                return acc;
            }, {});
            return inputValuesByRule;
        });
        const claimingClassifiers = applicableClassifiers.filter(([_code, rule]) => rule.tightCriteria(worksheet, this.initialWorkbookDetails, classifierInputs[rule.name]));
        const claimingCodes = claimingClassifiers.map(([code, _]) => code);

        if (claimingCodes.length > 1) {
            this.classificationEmitter.events.worksheetClassificationConflict.emit({
                workbookId: this.initialWorkbookDetails.id,
                worksheetId: worksheet.id,
                claimed: claimingCodes,
                considered: applicableCodes
            });
            return;
        }
        if (claimingCodes.length === 0) {
            this.classificationEmitter.events.worksheetUnclassified.emit({
                workbookId: this.initialWorkbookDetails.id,
                worksheetId: worksheet.id,
                considered: applicableCodes
            });
            return;
        }

        const claimingCode = claimingCodes[0];
        const claimingRule = rules[claimingCode];
        const classification: WorksheetClassification = {
            timestamp: Date.now(),
            code: claimingCode,
            rule: claimingRule.name,
            considered: applicableCodes
        };
        this.worksheetClassifications[worksheet.id] = classification;
        await this.classificationStore.save(this.worksheetClassifications);

        this.emitWorksheetClassification(this.initialWorkbookDetails.id, worksheet.id, classification, false);
    }

    private inputRangesForRule(worksheet:Excel.Worksheet, rule:WorksheetClassificationRule) {
        const ruleInputRanges = Object.entries(rule.inputs).reduce<Record<string,Excel.Range>>((acc, [inputName, inputRangeString]) => {
            acc[inputName] = worksheet.getRange(inputRangeString).load("values, address");
            return acc;
        },{});
        return ruleInputRanges;
    }

    private emitWorksheetClassification(workbookId: string, worksheetId: string, classification: WorksheetClassification, restored: boolean) {
        this.classificationEmitter.events.worksheetClassified.emit({ 
            workbookId, 
            worksheetId, 
            ...classification,
            restored
        });
    }
}
